import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, useScroll, useTransform, useMotionValue, useMotionValueEvent } from 'framer-motion';
import { ArrowRight } from 'react-iconly';
import profileImg from '../assets/hero/hype.webp';
import cornerAvatar from '../assets/profile.webp';
import CTA from '../components/CTA';
import HomeDeliverables from './HomeDeliverables';
import Testimonials from '../components/Testimonials';
import ScrollVideoPlayer from '../components/ScrollVideoPlayer';
import HomeWorkBento from './HomeWorkBento';
import { useLanguage } from '../context/LanguageContext';

const Home = () => {
  const { t } = useLanguage();

  // --- Hero portrait: travels from center-bottom → top-left corner on scroll ---
  const heroRef = useRef(null);
  const [vp, setVp] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1440,
    h: typeof window !== 'undefined' ? window.innerHeight : 900,
  }));
  const [nat, setNat] = useState({ w: 3, h: 4 }); // portrait fallback until the PNG loads
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  // heroProgress can read non-monotonically with the smooth-scroll + later pinned
  // sections (it briefly snaps backwards, which made the avatar revert to the big
  // character). Mirror it into a value that only ever advances — resetting only when
  // you're back near the very top — so once it morphs into the corner it stays.
  const morph = useMotionValue(0);
  useMotionValueEvent(heroProgress, 'change', (v) => {
    if (v < 0.02) morph.set(v);
    else if (v > morph.get()) morph.set(v);
  });

  // Mirror the CSS clamp(420px, 52vw, 780px) so the start frame matches the static layout.
  const renderedW = Math.min(780, Math.max(420, vp.w * 0.52));
  const renderedH = renderedW * (nat.h / nat.w);
  const cornerSize = 76; // resting avatar size in the corner
  const startX = (vp.w - renderedW) / 2; // centered horizontally
  const startY = vp.h - renderedH; // anchored to the viewport bottom
  // Land centered on the sidebar avatar slot (.v2-sidebar: left 24 + width 112 → center x≈80).
  const portraitX = useTransform(morph, [0, 0.85], [startX, 80 - cornerSize / 2]);
  const portraitY = useTransform(morph, [0, 0.85], [startY, 22]);
  const portraitScale = useTransform(morph, [0, 0.85], [1, cornerSize / renderedW]);
  // Keep it behind the hero cards at first, then lift above as it heads to the corner.
  const portraitZ = useTransform(morph, [0, 0.2, 0.21], [4, 4, 60]);
  // Near the corner, cross-fade the character into the circular ring avatar (matches About).
  const portraitOpacity = useTransform(morph, [0.68, 0.86], [1, 0]);
  const ringOpacity = useTransform(morph, [0.7, 0.9], [0, 1]);
  const ringScale = useTransform(morph, [0.7, 0.9], [0.6, 1]);

  return (
    <div>
      {/* 1. Hero Section */}
      <section ref={heroRef} className="hero-redesign dark-section">
        {/* Background Looping Text "ALVIN AHMAD" with spotlight mask */}
        <div className="hero-bg-text-container">
          <div className="hero-bg-text-marquee">
            {[...Array(6)].map((_, i) => (
              <span key={i} style={{ paddingRight: '80px' }}>ALVIN AHMAD</span>
            ))}
          </div>
        </div>

        {/* Spotlight beam — soft outer cone */}
        <div className="hero-spotlight-beam" />
        {/* Spotlight hot core */}
        <div className="hero-spotlight-core" />

        {/* Ambient Glow */}
        <div className="hero-glow-bg" />

        {/* Sparkles / Kilauan Cahaya */}
        <div className="hero-sparkle" style={{ top: '15%', left: '20%', animationDelay: '0s' }} />
        <div className="hero-sparkle" style={{ top: '25%', left: '75%', animationDelay: '1.2s' }} />
        <div className="hero-sparkle" style={{ top: '55%', left: '10%', animationDelay: '0.5s' }} />
        <div className="hero-sparkle" style={{ top: '65%', left: '85%', animationDelay: '1.8s' }} />
        <div className="hero-sparkle" style={{ top: '40%', left: '60%', animationDelay: '2.5s' }} />
        <div className="hero-sparkle" style={{ top: '80%', left: '30%', animationDelay: '0.8s' }} />

        <div className="v2-container" style={{ width: '100%', position: 'relative', zIndex: 10 }}>
          <div className="hero-content-grid">

            {/* Left Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', textAlign: 'left', zIndex: 10 }}>
              <div>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 14px',
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: '1px solid rgba(255, 255, 255, 0.22)',
                  borderRadius: '100px',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  fontSize: '10px',
                  fontWeight: '700',
                  letterSpacing: '0.08em',
                  color: '#fff',
                  textTransform: 'uppercase',
                  fontFamily: 'var(--v2-font-mono)',
                  marginBottom: '20px'
                }}>
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: '#4ade80',
                    boxShadow: '0 0 8px #4ade80'
                   }} />
                  {t('home.availableForWork')}
                </span>
                <h1 className="v2-display-xl" style={{
                  fontSize: 'clamp(32px, 4vw, 56px)',
                  lineHeight: '1.05',
                  letterSpacing: '-0.03em',
                  color: '#fff',
                  margin: 0,
                  fontFamily: 'var(--v2-font-display)',
                  fontWeight: '700',
                  textShadow: '0 4px 24px rgba(0, 0, 0, 0.35)'
                }}>
                  {t('home.role')}
                </h1>
                <p className="v2-body-l" style={{
                  marginTop: '20px',
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  maxWidth: '380px',
                  fontFamily: 'var(--v2-font-body)'
                }}>
                  {t('home.subtitle')}
                </p>
              </div>

              {/* Glassmorphic Satisfaction Card */}
              <div className="glass-card hero-card-satisfaction" style={{ maxWidth: '280px', width: '100%' }}>
                <div style={{ fontSize: '40px', fontWeight: '900', color: '#fff', lineHeight: '1', fontFamily: 'var(--v2-font-display)', textShadow: '0 2px 16px rgba(255, 255, 255, 0.25)' }}>96%</div>
                <div style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.75)', marginTop: '6px', fontFamily: 'var(--v2-font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '600' }}>
                  {t('home.satisfaction')}
                </div>
                {/* Vertical Mock Bar Chart */}
                <div className="bar-chart-container">
                  {[15, 22, 28, 20, 14, 25, 30, 24, 18, 22, 26, 15, 20, 24, 28].map((height, i) => (
                    <div key={i} className="bar-chart-bar" style={{ height: `${height}px` }} />
                  ))}
                </div>
              </div>
            </div>

            {/* Center column intentionally empty — portrait is absolutely positioned to bottom of section */}
            <div />

            {/* Right Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', textAlign: 'left', zIndex: 10, alignItems: 'flex-start' }}>

              {/* Happy Customers Card */}
              <div className="glass-card" style={{ width: '100%', maxWidth: '280px', padding: '14px 18px', borderRadius: '100px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div className="avatar-stack">
                  <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=80&h=80&fit=crop&crop=faces" alt="Client 1" className="avatar-stack-img" />
                  <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=faces" alt="Client 2" className="avatar-stack-img" />
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=faces" alt="Client 3" className="avatar-stack-img" />
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ff7722, #ff3b30)',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    color: '#fff',
                    fontSize: '9px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '-8px',
                    fontFamily: 'var(--v2-font-mono)'
                  }}>
                    10+
                  </div>
                </div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#fff', fontFamily: 'var(--v2-font-body)', letterSpacing: '0.01em' }}>
                  {t('home.happyCustomers')}
                </span>
              </div>

              {/* Floating Badge Cloud */}
              <div style={{ display: 'flex', flexWrap: 'wrap', maxWidth: '300px', margin: '4px -4px' }}>
                <span className="hero-badge-tag">React & Next.js</span>
                <span className="hero-badge-tag">Node.js</span>
                <span className="hero-badge-tag">Full-Stack Dev</span>
                <span className="hero-badge-tag">API Integration</span>
                <span className="hero-badge-tag">Mobile Dev</span>
              </div>

              {/* Glassmorphic Experience Card */}
              <div className="glass-card hero-card-experience" style={{ width: '100%', maxWidth: '280px' }}>
                <div style={{ fontSize: '40px', fontWeight: '900', color: '#fff', lineHeight: '1', fontFamily: 'var(--v2-font-display)', textShadow: '0 2px 16px rgba(255, 255, 255, 0.25)' }}>5 {t('home.years') || 'Years'}</div>
                <div style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.75)', marginTop: '6px', fontFamily: 'var(--v2-font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '600' }}>
                  {t('home.experience')}
                </div>
                <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)', margin: '12px 0 0 0', lineHeight: '1.5', fontFamily: 'var(--v2-font-body)' }}>
                  {t('home.experienceDesc')}
                </p>
              </div>

            </div>

          </div>
        </div>

        </section>

      {/* Portrait + ring avatar are portalled to <body> to escape all parent stacking
          contexts (HomeDeliverables has perspective + will-change on cards which can
          create a paint layer that visually buries fixed children of the same ancestor). */}
      {createPortal(
        <>
          <motion.div
            className="hero-portrait-wrapper"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 'auto',
              width: 'fit-content',
              maxWidth: 'none',
              x: portraitX,
              y: portraitY,
              scale: portraitScale,
              zIndex: portraitZ,
              opacity: portraitOpacity,
              transformOrigin: 'top left',
              pointerEvents: 'none',
            }}
          >
            <img
              src={profileImg}
              alt="Alvin Ahmad"
              className="hero-portrait-img"
              onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth || 3, h: e.currentTarget.naturalHeight || 4 })}
            />
          </motion.div>

          <motion.div
            aria-hidden="true"
            style={{
              position: 'fixed', top: '32px', left: '46px', width: '68px', height: '68px',
              zIndex: 101, opacity: ringOpacity, scale: ringScale, pointerEvents: 'none',
            }}
          >
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
              WebkitMaskImage: 'radial-gradient(circle, transparent 30px, black 31px)',
              maskImage: 'radial-gradient(circle, transparent 30px, black 31px)',
            }} />
            <div style={{ position: 'absolute', top: '5px', left: '5px', right: '5px', bottom: '5px', borderRadius: '50%', overflow: 'hidden' }}>
              <img src={cornerAvatar} alt="Alvin Ahmad" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          </motion.div>
        </>,
        document.body
      )}

      {/* 2 & 3. Expanding Showcase & Dark Transition */}
      <ScrollVideoPlayer src="https://ixezilyqdbarjbqfoczc.supabase.co/storage/v1/object/public/assets/drone.mp4" overlayText="alvinahmad.dev" />

      {/* HomeDeliverables Section */}
      <HomeDeliverables />

      {/* 4. Featured Work Title Section */}
      <section id="featured-work" className="v2-section v2-container" style={{ textAlign: 'center', paddingBottom: '0' }}>
        <h2 className="v2-display-l" style={{ marginBottom: '24px' }}>
          {t('home.featuredWork') ? t('home.featuredWork').split(' ')[0] : 'Featured'}<br/>
          <span style={{ color: 'var(--v2-color-taupe)' }}>{t('home.featuredWork') ? t('home.featuredWork').split(' ').slice(1).join(' ') : 'work'}</span>
        </h2>
        <div style={{ margin: '40px auto' }}>
          <span style={{ display: 'inline-block', transform: 'rotate(90deg)' }}>
            <ArrowRight size={24} set="curved" weight="bold" />
          </span>
        </div>
        <p className="v2-body-l" style={{ maxWidth: '600px', margin: '0 auto', color: 'var(--v2-color-taupe)' }}>
          {t('home.featuredWorkDesc')}
        </p>
      </section>

      {/* 5. Work Grid Section */}
      <div style={{ paddingTop: '64px' }}>
        <HomeWorkBento />
      </div>

      {/* Testimonial Section */}
      <div style={{ paddingTop: '120px' }}>
        <Testimonials />
      </div>

      {/* 6. Footer / CTA Section */}
      <div style={{ paddingTop: '64px' }}>
        <CTA />
      </div>
    </div>
  );
};

export default Home;
